// Where the linked browser's keys live: IndexedDB, which can hold CryptoKey objects by
// structured clone while keeping them non-extractable (docs/09 "Web UI"). One record,
// because a browser profile is one device; the account key, when this browser holds it
// (D41), is a second record in the same store (account/store.ts).
import type { DeviceKeys } from "./keys";

export interface LinkedDevice {
  /** The API's device id (ULID string). */
  deviceId: string;
  /** The same id as the relay carries it. */
  deviceIdBytes: Uint8Array;
  name: string;
  keys: DeviceKeys;
}

const DB_NAME = "ocs";
const STORE = "device";
const KEY = "linked";

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function openDb(): Promise<IDBDatabase> {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => {
    if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
  };
  return request(req);
}

/** One request against the `device` object store. */
export async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await request(run(db.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    db.close();
  }
}

export async function saveLinkedDevice(device: LinkedDevice): Promise<void> {
  await withStore("readwrite", (s) => s.put(device, KEY));
}

export async function loadLinkedDevice(): Promise<LinkedDevice | undefined> {
  if (typeof indexedDB === "undefined") return undefined;
  const value: unknown = await withStore("readonly", (s) => s.get(KEY));
  return isLinkedDevice(value) ? value : undefined;
}

export async function forgetLinkedDevice(): Promise<void> {
  await withStore("readwrite", (s) => s.delete(KEY));
}

function isLinkedDevice(v: unknown): v is LinkedDevice {
  if (!v || typeof v !== "object") return false;
  const d = v as Partial<LinkedDevice>;
  return (
    typeof d.deviceId === "string" &&
    d.deviceIdBytes instanceof Uint8Array &&
    typeof d.name === "string" &&
    !!d.keys &&
    typeof d.keys === "object" &&
    "encryption" in d.keys &&
    "auth" in d.keys
  );
}
